import {createSlice, isAnyOf} from '@reduxjs/toolkit';
import {type UpcomingEvent} from 'api/events/events.types';
import {type PaginationPage} from 'api/api.types';
import {apiDateFormat, displayDateFormat} from 'utils/date-time';
import dayjs from 'utils/dayjs';
import {
    type CompPassConfirmationErrors,
    type IndividualError,
    type RecipientError,
} from 'pages/comp-pass/components/comp-pass-wizard/comp-pass-wizard.types';
import {ADD_RECIPIENTS_METHOD} from 'pages/comp-pass/components/comp-pass-wizard/utils';
import {type SelectOption} from 'pages/comp-pass/comp-pass.types';

import {
    confirmAndSendCompPasses,
    decrementCreationStep,
    deleteCompPassData,
    getCompPassesData,
    getCompPassSummaryCountData,
    getPassCreationHistory,
    incrementCreationStep,
    resetCreationFlow,
    setActiveTabIndex,
    setCreationMethod,
    setCreationStep,
    setGlobalError,
    updateListingFilters,
} from 'pages/comp-pass/store/comp-pass-actions';
import {
    compPassBatch,
    compPassRecipient,
    passListAdapter,
} from 'pages/comp-pass/store/comp-pass-adapters';
import {
    deleteRecipient,
    deleteUploadedCsvFile,
    setCsvUploadError,
    setDisclosureAcknowledged,
    updateAndValidateManualCompPassRecipients,
    validateAndAddCsvCompPassRecipients,
} from 'pages/comp-pass/store/recipient-actions';
import {
    getEventFilterOptions,
    getUpcomingEventsData,
    updateEventFilters,
    updateEventSelection,
} from 'pages/comp-pass/store/event-selection-actions';
import {compPassEventsAdapter} from 'pages/comp-pass/store/event-selection-adapters';

const now = dayjs();

export const initialFromDate = now.format(displayDateFormat);
export const initialToDate = now.add(90, 'day').format(displayDateFormat);

export interface CompPassState {
    activeTabIndex: number;
    aggregateCounts: {
        counts: {
            day: number;
            month: number;
            year: number;
        };
        date: string;
    };
    filterOptions: {
        compPassBatchId?: string;
        destinationIds?: SelectOption[];
        email?: string;
        eventTypeIds?: SelectOption[];
        fromDate: string;
        isLoading: boolean;
        toDate: string;
    };
    isLoading: boolean;
    passBatches: {
        batches: ReturnType<typeof compPassBatch.getInitialState>;
        isLoading: boolean;
        meta: PaginationPage;
    };
    passCreation: {
        addRecipients: {
            fileName?: string;
            isDisclosureAcknowledged: boolean;
            method: string;
            recipients: ReturnType<typeof compPassRecipient.getInitialState>;
        };
        error: string;
        errors: {
            addRecipients: RecipientError;
            confirmation: CompPassConfirmationErrors;
            selectEvents: IndividualError;
        };
        selectEvents: {
            appliedFilters: {
                destinationIds?: number[];
                eventTypeIds?: number[];
                fromDate?: string;
                numFiltersApplied: number;
                toDate?: string;
            };
            data: {
                events: ReturnType<
                    typeof compPassEventsAdapter.getInitialState
                >;
                isLoading: boolean;
                meta: PaginationPage;
            };
            selectedEvents: UpcomingEvent[];
        };
        step: number;
    };
    passListing: {
        data: ReturnType<typeof passListAdapter.getInitialState>;
        filters: {
            compPassBatchId?: number;
            destinationIds?: number[];
            email?: string;
            eventTypeIds?: number[];
            fromDate: string;
            numFiltersApplied: number;
            toDate: string;
        };
        isLoading: boolean;
        meta: PaginationPage;
    };
}

export const initialState: CompPassState = {
    activeTabIndex: 0,
    aggregateCounts: {
        counts: {
            day: 0,
            month: 0,
            year: 0,
        },
        date: now.format(apiDateFormat),
    },
    filterOptions: {
        fromDate: initialFromDate,
        toDate: initialToDate,
        destinationIds: [],
        eventTypeIds: [],
        isLoading: false,
    },
    isLoading: false,
    passBatches: {
        batches: compPassBatch.getInitialState(),
        isLoading: false,
        meta: {
            nextPage: null,
            page: 1,
            pages: 1,
            perPage: 25,
            prevPage: null,
            total: 0,
        },
    },
    passCreation: {
        addRecipients: {
            fileName: null,
            isDisclosureAcknowledged: false,
            method: ADD_RECIPIENTS_METHOD.UPLOAD,
            recipients: compPassRecipient.getInitialState(),
        },
        error: null,
        errors: {
            addRecipients: null,
            confirmation: null,
            selectEvents: null,
        },
        selectEvents: {
            appliedFilters: {
                destinationIds: [],
                eventTypeIds: [],
                fromDate: initialFromDate,
                numFiltersApplied: 0,
                toDate: initialToDate,
            },
            data: {
                events: compPassEventsAdapter.getInitialState(),
                isLoading: false,
                meta: {
                    nextPage: null,
                    page: 1,
                    pages: 1,
                    perPage: 25,
                    total: 25,
                },
            },
            selectedEvents: [],
        },
        step: 0,
    },
    passListing: {
        data: passListAdapter.getInitialState(),
        filters: {
            compPassBatchId: null,
            destinationIds: [],
            email: '',
            eventTypeIds: [],
            fromDate: initialFromDate,
            numFiltersApplied: 0,
            toDate: initialToDate,
        },
        isLoading: false,
        meta: {
            nextPage: null,
            page: 1,
            pages: 1,
            perPage: 25,
            prevPage: null,
            total: 25,
        },
    },
};

const compPassSlice = createSlice({
    name: 'compPass',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(setActiveTabIndex, (state, action) => {
                state.activeTabIndex = action.payload;
            })
            .addCase(getCompPassSummaryCountData.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getCompPassSummaryCountData.fulfilled, (state, action) => {
                state.isLoading = false;

                state.aggregateCounts = action.payload;
            })
            .addCase(getPassCreationHistory.pending, (state) => {
                state.passBatches.isLoading = true;
            })
            .addCase(getPassCreationHistory.fulfilled, (state, action) => {
                compPassBatch.setAll(
                    state.passBatches.batches,
                    action.payload.data.results
                );
                state.passBatches.meta = action.payload.meta;
                state.passBatches.isLoading = false;
            })
            .addCase(getCompPassesData.fulfilled, (state, {payload}) => {
                passListAdapter.setAll(
                    state.passListing.data,
                    payload.data.results
                );
                state.passListing.meta = payload.meta;
                state.passListing.isLoading = false;
            })
            .addCase(getCompPassesData.pending, (state) => {
                state.passListing.isLoading = true;
            })
            .addCase(getCompPassesData.rejected, (state, action) => {
                if (action.meta.aborted) {
                    return;
                }

                state.passListing.isLoading = false;
            })
            .addCase(deleteCompPassData.fulfilled, (state, {payload}) => {
                passListAdapter.removeOne(state.passListing.data, payload);
                state.passListing.isLoading = false;
            })
            .addCase(deleteCompPassData.pending, (state) => {
                state.passListing.isLoading = true;
            })
            .addCase(deleteCompPassData.rejected, (state) => {
                state.passListing.isLoading = false;
            })
            .addCase(updateListingFilters, (state, action) => {
                state.passListing.filters = action.payload;
            })
            // Creation Step Actions
            .addCase(incrementCreationStep, (state) => {
                state.passCreation.step++;
            })
            .addCase(decrementCreationStep, (state) => {
                state.passCreation.step--;
            })
            .addCase(setCreationStep, (state, action) => {
                state.passCreation.step = action.payload;
            })
            .addCase(resetCreationFlow, (state) => {
                state.passCreation = initialState.passCreation;
            })
            .addCase(setGlobalError, (state, {payload}) => {
                state.passCreation.error = payload;
            })
            // Event Selection Actions
            .addCase(updateEventFilters, (state, action) => {
                state.passCreation.selectEvents.appliedFilters = action.payload;
            })
            .addCase(getEventFilterOptions.pending, (state) => {
                state.filterOptions.isLoading = true;
            })
            .addCase(getEventFilterOptions.fulfilled, (state, {payload}) => {
                const {venueIds, eventTypeIds} = payload;

                state.filterOptions = {
                    ...state.filterOptions,
                    destinationIds: venueIds,
                    eventTypeIds,
                    isLoading: false,
                };
            })
            .addCase(getUpcomingEventsData.fulfilled, (state, {payload}) => {
                const {data, meta} = payload;

                compPassEventsAdapter.setAll(
                    state.passCreation.selectEvents.data.events,
                    data
                );
                state.passCreation.selectEvents.data.isLoading = false;
                state.passCreation.selectEvents.data.meta = meta;
            })
            .addCase(getUpcomingEventsData.pending, (state) => {
                state.passCreation.selectEvents.data.isLoading = true;
            })
            .addCase(getUpcomingEventsData.rejected, (state, action) => {
                if (action.meta.aborted) {
                    return;
                }

                state.passCreation.selectEvents.data.isLoading = false;
            })
            .addCase(updateEventSelection, (state, {payload}) => {
                state.passCreation.selectEvents.selectedEvents = payload;
                state.passCreation.error = null;
            })
            // Add Recipients Actions
            .addCase(setCreationMethod, (state, {payload}) => {
                state.passCreation.addRecipients.method = payload;

                compPassRecipient.removeAll(
                    state.passCreation.addRecipients.recipients
                );
                state.passCreation.addRecipients.fileName = null;
                state.passCreation.errors.addRecipients = null;
                state.passCreation.error = null;
            })
            .addCase(setDisclosureAcknowledged, (state, {payload}) => {
                state.passCreation.addRecipients.isDisclosureAcknowledged =
                    payload;
                state.passCreation.error = null;
            })
            // Manual Step
            .addCase(
                updateAndValidateManualCompPassRecipients.fulfilled,
                (state, {payload}) => {
                    compPassRecipient.upsertMany(
                        state.passCreation.addRecipients.recipients,
                        payload
                    );

                    state.passCreation.errors.addRecipients = null;
                    state.passCreation.error = null;
                }
            )
            .addCase(
                updateAndValidateManualCompPassRecipients.rejected,
                (state, {payload}) => {
                    const {errors, recipients} = payload;

                    state.passCreation.errors.addRecipients = errors;
                    compPassRecipient.upsertMany(
                        state.passCreation.addRecipients.recipients,
                        recipients
                    );
                }
            )
            .addCase(deleteRecipient.fulfilled, (state, {payload}) => {
                const {addRecipientsErrors, recipients} = payload;

                compPassRecipient.setAll(
                    state.passCreation.addRecipients.recipients,
                    recipients
                );

                state.passCreation.errors.addRecipients = addRecipientsErrors;
            })
            // CSV Upload
            .addCase(
                validateAndAddCsvCompPassRecipients.fulfilled,
                (state, {payload}) => {
                    const {recipients, fileName} = payload;

                    compPassRecipient.setAll(
                        state.passCreation.addRecipients.recipients,
                        recipients
                    );
                    state.passCreation.addRecipients.fileName = fileName;

                    state.passCreation.errors.addRecipients = null;
                    state.passCreation.error = null;
                }
            )
            .addCase(deleteUploadedCsvFile, (state) => {
                compPassRecipient.removeAll(
                    state.passCreation.addRecipients.recipients
                );

                state.passCreation.addRecipients.fileName = null;
            })
            .addCase(confirmAndSendCompPasses.fulfilled, (state, {payload}) => {
                const {data, meta} = payload;

                compPassBatch.setAll(state.passBatches.batches, data);
                state.passBatches.isLoading = false;
                state.passBatches.meta = meta;
            })
            .addCase(confirmAndSendCompPasses.rejected, (state, {payload}) => {
                state.passBatches.isLoading = false;
                state.passCreation.errors.confirmation = payload;
            })
            .addCase(confirmAndSendCompPasses.pending, (state) => {
                state.passBatches.isLoading = true;
            })
            .addMatcher(
                isAnyOf(
                    validateAndAddCsvCompPassRecipients.rejected,
                    setCsvUploadError
                ),
                (state, {payload}) => {
                    state.passCreation.errors.addRecipients = payload;
                }
            );
    },
});

export default compPassSlice.reducer;
